import type { IH5PUploadFile, IContentBrowserState } from './types';

import { match } from 'path-to-regexp';

import { paths } from 'src/routes/paths';

import * as ActionTypes from 'src/state/action-types';

// Load uploads from localStorage
function loadUploadsFromStorage(): Record<string, IH5PUploadFile> {
  try {
    const stored = localStorage.getItem('contentBrowser:uploads');
    if (stored) {
      const uploads = JSON.parse(stored);
      // Filter out completed or failed uploads older than 1 hour
      const now = Date.now();
      const filtered: Record<string, IH5PUploadFile> = {};

      Object.entries(uploads).forEach(([id, upload]: [string, any]) => {
        const age = now - upload.startedAt;
        const isOld = age > 60 * 60 * 1000; // 1 hour
        const isFinished =
          upload.uploadProgress.stage === 'completed' || upload.uploadProgress.stage === 'failed';

        // Keep if not finished or not old
        if (!isFinished || !isOld) {
          filtered[id] = upload;
        }
      });

      return filtered;
    }
  } catch (error) {
    console.error('Failed to load uploads from localStorage:', error);
  }
  return {};
}

// Save uploads to localStorage
function saveUploadsToStorage(uploads: Record<string, IH5PUploadFile>) {
  try {
    localStorage.setItem('contentBrowser:uploads', JSON.stringify(uploads));
  } catch (error) {
    console.error('Failed to save uploads to localStorage:', error);
  }
}

export const initialState: IContentBrowserState = {
  currentFolderId: 'root',
  displayMode: 'list',
  selectedFolders: [],
  selectedContent: [],
  clipboardFolders: [],
  clipboardContent: [],
  clipboardAction: null,
  pageInfo: null,
  breadcrumbs: [],
  loading: false,
  error: null,
  searchQuery: '',
  searchResults: [],
  viewMode: 'folder',
  uploads: loadUploadsFromStorage(),
};

const folder_matcher = match(paths.dashboard.folder(':workspaceId', ':folderId'));

export default function contentBrowserReducer(
  state: IContentBrowserState = initialState,
  action: ActionTypes.ActionTypes
): IContentBrowserState {
  switch (action.type) {
    case ActionTypes.CONTENT_BROWSER_SET_CURRENT_FOLDER:
      // Only update if folderId actually changed to prevent unnecessary re-renders
      if (state.currentFolderId === action.payload.folderId) {
        return state;
      }

      return {
        ...state,
        currentFolderId: action.payload.folderId,
        searchQuery: '',
        searchResults: [],
        viewMode: 'folder',
      };

    case ActionTypes.APP_NAVIGATED:
      // eslint-disable-next-line no-case-declarations
      const matchResult = folder_matcher(action.payload.path);
      if (matchResult && matchResult.params && matchResult.params.folderId) {
        const { folderId } = matchResult.params;

        // Only update if folderId actually changed to prevent unnecessary re-renders
        if (state.currentFolderId === folderId) {
          return state;
        }

        return {
          ...state,
          currentFolderId: folderId as string,
          searchQuery: '',
          searchResults: [],
          viewMode: 'folder',
        };
      }
      return state;

    case ActionTypes.CONTENT_BROWSER_SET_DISPLAY_MODE:
      return {
        ...state,
        displayMode: action.payload.displayMode,
      };

    case ActionTypes.WORKSPACE_WORKSPACE_SELECTED:
      // Only reset to root if not already at root to prevent unnecessary re-renders
      if (
        state.currentFolderId === 'root' &&
        state.searchQuery === '' &&
        state.searchResults.length === 0 &&
        state.viewMode === 'folder'
      ) {
        return state;
      }

      return {
        ...state,
        currentFolderId: 'root',
        searchQuery: '',
        searchResults: [],
        viewMode: 'folder',
      };

    case ActionTypes.CONTENT_BROWSER_SET_SELECTED_FOLDERS:
      return {
        ...state,
        selectedFolders: action.payload.folderIds,
      };

    case ActionTypes.CONTENT_BROWSER_SET_SELECTED_CONTENT:
      return {
        ...state,
        selectedContent: action.payload.contentIds,
      };

    case ActionTypes.CONTENT_BROWSER_RESET_SELECTION:
      return {
        ...state,
        selectedFolders: [],
        selectedContent: [],
      };

    case ActionTypes.CONTENT_BROWSER_CUT_ITEMS:
      return {
        ...state,
        clipboardFolders: action.payload.folderIds,
        clipboardContent: action.payload.contentIds,
        clipboardAction: 'cut',
        selectedFolders: [],
        selectedContent: [],
      };

    case ActionTypes.CONTENT_BROWSER_PASTE_ITEMS:
      // Paste action is handled by async logic, so we just clear selection here
      return {
        ...state,
        selectedFolders: [],
        selectedContent: [],
      };

    case ActionTypes.CONTENT_BROWSER_CLEAR_CLIPBOARD:
      return {
        ...state,
        clipboardFolders: [],
        clipboardContent: [],
        clipboardAction: null,
      };

    case ActionTypes.CONTENT_BROWSER_REMOVE_CLIPBOARD_ITEM:
      // Business logic: Remove item from clipboard based on itemType
      // eslint-disable-next-line no-case-declarations
      const { itemType, itemId } = action.payload;
      // eslint-disable-next-line no-case-declarations
      const newClipboardFolders =
        itemType === 'folder'
          ? state.clipboardFolders.filter((id) => id !== itemId)
          : state.clipboardFolders;
      // eslint-disable-next-line no-case-declarations
      const newClipboardContent =
        itemType === 'content'
          ? state.clipboardContent.filter((id) => id !== itemId)
          : state.clipboardContent;

      // Business rule: If clipboard becomes empty, clear clipboard action
      // eslint-disable-next-line no-case-declarations
      const clipboardAction =
        newClipboardFolders.length === 0 && newClipboardContent.length === 0
          ? null
          : state.clipboardAction;

      return {
        ...state,
        clipboardFolders: newClipboardFolders,
        clipboardContent: newClipboardContent,
        clipboardAction,
      };

    case ActionTypes.CONTENT_BROWSER_SELECT_FOLDER:
      return {
        ...state,
        selectedFolders: state.selectedFolders.includes(action.payload.folderId)
          ? state.selectedFolders.filter((id) => id !== action.payload.folderId)
          : [...state.selectedFolders, action.payload.folderId],
      };

    case ActionTypes.CONTENT_BROWSER_SELECT_CONTENT:
      return {
        ...state,
        selectedContent: state.selectedContent.includes(action.payload.contentId)
          ? state.selectedContent.filter((id) => id !== action.payload.contentId)
          : [...state.selectedContent, action.payload.contentId],
      };

    case ActionTypes.CONTENT_BROWSER_SEARCH_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ActionTypes.CONTENT_BROWSER_SEARCH_SUCCESS:
      // Append results for pagination (page > 1), replace for fresh searches (page 1)
      return {
        ...state,
        loading: false,
        pageInfo: action.payload.pageInfo,
        searchResults:
          action.payload.params.currentPage && action.payload.params.currentPage > 1
            ? [...state.searchResults, ...action.payload.results]
            : action.payload.results,
        viewMode: action.payload.params.query ? 'search' : 'folder',
        error: null,
      };

    case ActionTypes.CONTENT_BROWSER_SEARCH_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload,
      };

    case ActionTypes.CONTENT_BROWSER_GET_BREADCRUMBS_REQUEST:
      return state;

    case ActionTypes.CONTENT_BROWSER_GET_BREADCRUMBS_SUCCESS:
      return {
        ...state,
        breadcrumbs: action.payload.breadcrumbs,
      };

    case ActionTypes.CONTENT_BROWSER_GET_BREADCRUMBS_FAILURE:
      return state;

    case ActionTypes.CONTENT_BROWSER_SET_SEARCH_QUERY:
      return {
        ...state,
        searchQuery: action.payload.query,
      };

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
      };

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_SUCCESS:
      return {
        ...state,
        loading: false,
        clipboardFolders: [],
        clipboardContent: [],
        clipboardAction: null,
        error: null,
      };

    case ActionTypes.CONTENT_BROWSER_MOVE_ITEMS_FAILURE:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
      };

    case ActionTypes.CONTENT_BROWSER_TOGGLE_SELECT_ALL:
      // Business logic: Determine if all visible items are currently selected
      // eslint-disable-next-line no-case-declarations
      const { visibleFolderIds, visibleContentIds } = action.payload;

      // Check if all visible folders are selected
      // eslint-disable-next-line no-case-declarations
      const allVisibleFoldersSelected = visibleFolderIds.every((id) =>
        state.selectedFolders.includes(id)
      );

      // Check if all visible content are selected
      // eslint-disable-next-line no-case-declarations
      const allVisibleContentSelected = visibleContentIds.every((id) =>
        state.selectedContent.includes(id)
      );

      // Business rule: If all visible items are selected, deselect all. Otherwise, select all visible items.
      // eslint-disable-next-line no-case-declarations
      const allSelected =
        allVisibleFoldersSelected &&
        allVisibleContentSelected &&
        (visibleFolderIds.length > 0 || visibleContentIds.length > 0);

      if (allSelected) {
        // Deselect all
        return {
          ...state,
          selectedFolders: [],
          selectedContent: [],
        };
      } else {
        // Select all visible items
        return {
          ...state,
          selectedFolders: visibleFolderIds,
          selectedContent: visibleContentIds,
        };
      }

    case ActionTypes.CONTENT_DELETE_SUCCESS: {
      // Clear selection and clipboard after successful deletion

      const deletedIds = action.payload.contentIds;
      return {
        ...state,
        selectedContent: state.selectedContent.filter((id) => !deletedIds.includes(id)),
        clipboardContent: state.clipboardContent.filter((id) => !deletedIds.includes(id)),
        // Clear clipboard action if clipboard becomes empty
        clipboardAction:
          state.clipboardContent.filter((id) => !deletedIds.includes(id)).length === 0 &&
          state.clipboardFolders.length === 0
            ? null
            : state.clipboardAction,
      };
    }

    case ActionTypes.FOLDERS_DELETEFOLDER_SUCCESS: {
      // Clear selection and clipboard after successful folder deletion

      const deletedFolderIds = action.payload.folderIds;
      return {
        ...state,
        selectedFolders: state.selectedFolders.filter((id) => !deletedFolderIds.includes(id)),
        clipboardFolders: state.clipboardFolders.filter((id) => !deletedFolderIds.includes(id)),
        // Clear clipboard action if clipboard becomes empty
        clipboardAction:
          state.clipboardContent.length === 0 &&
          state.clipboardFolders.filter((id) => !deletedFolderIds.includes(id)).length === 0
            ? null
            : state.clipboardAction,
      };
    }

    // Upload Actions
    case ActionTypes.CONTENT_BROWSER_UPLOAD_STARTED: {
      const { uploadId, fileName, fileSize, fileType, workspaceId, folderId } = action.payload;
      const newUploads = {
        ...state.uploads,
        [uploadId]: {
          id: uploadId,
          file: {
            name: fileName,
            size: fileSize,
            type: fileType,
          },
          workspaceId,
          folderId,
          uploadProgress: {
            stage: 'idle' as const,
            progress: 0,
          },
          startedAt: Date.now(),
        },
      };

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOAD_PROGRESS_UPDATED: {
      const { uploadId, progress } = action.payload;
      const upload = state.uploads[uploadId];

      if (!upload) {
        return state;
      }

      const newUploads = {
        ...state.uploads,
        [uploadId]: {
          ...upload,
          uploadProgress: {
            ...upload.uploadProgress,
            ...progress,
          },
        },
      };

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOAD_COMPLETED: {
      const { uploadId, contentId } = action.payload;
      const upload = state.uploads[uploadId];

      if (!upload) {
        return state;
      }

      const newUploads = {
        ...state.uploads,
        [uploadId]: {
          ...upload,
          uploadProgress: {
            ...upload.uploadProgress,
            stage: 'completed' as const,
            progress: 100,
            contentId,
          },
        },
      };

      // Auto-remove completed uploads after a short delay (handled by component)
      // But first save to localStorage
      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOAD_FAILED: {
      const { uploadId, error } = action.payload;
      const upload = state.uploads[uploadId];

      if (!upload) {
        return state;
      }

      const newUploads = {
        ...state.uploads,
        [uploadId]: {
          ...upload,
          uploadProgress: {
            ...upload.uploadProgress,
            stage: 'failed' as const,
            error,
          },
        },
      };

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOAD_CANCELLED: {
      const { uploadId } = action.payload;
      const newUploads = { ...state.uploads };
      delete newUploads[uploadId];

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOAD_REMOVED: {
      const { uploadId } = action.payload;
      const newUploads = { ...state.uploads };
      delete newUploads[uploadId];

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    case ActionTypes.CONTENT_BROWSER_UPLOADS_CLEARED: {
      // Remove all completed and failed uploads
      const newUploads: Record<string, IH5PUploadFile> = {};

      Object.entries(state.uploads).forEach(([id, upload]) => {
        if (
          upload.uploadProgress.stage !== 'completed' &&
          upload.uploadProgress.stage !== 'failed'
        ) {
          newUploads[id] = upload;
        }
      });

      saveUploadsToStorage(newUploads);

      return {
        ...state,
        uploads: newUploads,
      };
    }

    default:
      return state;
  }
}
